import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import { useNavigate } from "react-router-dom";
import ClientLayout from "../../components/portal/ClientLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
  Briefcase,
  Clock,
  CheckCircle,
  Pause,
  AlertCircle,
  Loader2,
  Calendar,
  Building2,
  ArrowRight,
} from "lucide-react";

const ClientMyProjects = () => {
  const { token, API } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API}/client/projects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to fetch projects");
      }
      
      const data = await response.json();
      setProjects(data.projects || []);
    } catch (err) {
      setError(err.message);
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return (
          <Badge className="bg-green-100 text-green-700 border-green-300">
            <CheckCircle className="h-3 w-3 mr-1" />
            Active
          </Badge>
        );
      case "on_hold":
        return (
          <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300">
            <Pause className="h-3 w-3 mr-1" />
            On Hold
          </Badge>
        );
      case "completed":
        return (
          <Badge className="bg-blue-100 text-blue-700 border-blue-300">
            <CheckCircle className="h-3 w-3 mr-1" />
            Completed
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            {status || "Unknown"}
          </Badge>
        );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const activeProjects = projects.filter((p) => p.status === "active");
  const otherProjects = projects.filter((p) => p.status !== "active");

  return (
    <ClientLayout activeNav="my-projects">
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-xl font-bold text-rnb-navy" data-testid="projects-title">My Projects</h1>
            <p className="text-sm text-gray-400 mt-0.5">View and track your consulting projects.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12" data-testid="loading-spinner">
            <div className="w-8 h-8 border-2 border-rnb-teal/30 border-t-rnb-teal rounded-full animate-spin"></div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl border border-red-200 p-8 text-center" data-testid="error-card">
            <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
            <p className="text-red-600 font-medium text-sm mb-1">Failed to load projects</p>
            <p className="text-red-400 text-xs">{error}</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 shadow-card p-12 text-center" data-testid="no-projects-card">
            <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-rnb-navy mb-1">No Projects Yet</h3>
            <p className="text-sm text-gray-400">You don&apos;t have any projects assigned yet. Contact your consultant to get started.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Active Projects */}
            {activeProjects.length > 0 && (
              <div data-testid="active-projects-section">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 bg-green-50 rounded flex items-center justify-center">
                    <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                  </div>
                  <span className="text-sm font-semibold text-rnb-navy">Active Projects ({activeProjects.length})</span>
                </div>
                <div className="space-y-3">
                  {activeProjects.map((project) => (
                    <div
                      key={project.project_id}
                      className="bg-white rounded-xl border border-gray-100 shadow-card hover:shadow-card-hover transition-shadow cursor-pointer group p-5"
                      data-testid={`project-${project.project_id}`}
                      onClick={() => navigate(`/portal/time?project_id=${project.project_id}`)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1.5">
                            <h3 className="text-sm font-semibold text-rnb-navy">{project.name}</h3>
                            {getStatusBadge(project.status)}
                          </div>
                          {project.description && (
                            <p className="text-xs text-gray-400 mb-3">{project.description}</p>
                          )}
                          <div className="flex flex-wrap gap-4 text-xs text-gray-400">
                            {project.company_name && (
                              <span className="flex items-center gap-1"><Building2 className="h-3 w-3" /> {project.company_name}</span>
                            )}
                            {project.current_phase && (
                              <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" /> {project.current_phase}</span>
                            )}
                            {project.monthly_hours && (
                              <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {project.monthly_hours} hrs/month</span>
                            )}
                            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Started {formatDate(project.created_at)}</span>
                          </div>
                        </div>
                        <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-rnb-blue transition-colors flex-shrink-0 mt-1" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Other Projects */}
            {otherProjects.length > 0 && (
              <div data-testid="other-projects-section">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 bg-gray-50 rounded flex items-center justify-center">
                    <Briefcase className="h-3.5 w-3.5 text-gray-400" />
                  </div>
                  <span className="text-sm font-semibold text-rnb-navy">Other Projects ({otherProjects.length})</span>
                </div>
                <div className="space-y-2">
                  {otherProjects.map((project) => (
                    <div
                      key={project.project_id}
                      className="bg-white rounded-xl border border-gray-100 p-4"
                      data-testid={`project-${project.project_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-0.5">
                            <h3 className="font-medium text-rnb-navy text-sm">{project.name}</h3>
                            {getStatusBadge(project.status)}
                          </div>
                          {project.description && (
                            <p className="text-xs text-gray-400 line-clamp-1">{project.description}</p>
                          )}
                        </div>
                        <span className="text-xs text-gray-400">{formatDate(project.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </ClientLayout>
  );
};

export default ClientMyProjects;
